import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { StackedToasts, ToastPositionEnum } from "kubra-ux-forge";

export type ToastType = "success" | "error" | "info" | "warning";

export interface ToastMessage {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  addToast: (message: string, type: ToastType) => void;
  removeToast: (id: string) => void;
  toasts: ToastMessage[];
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("context not found, useToast must be within a ToastProvider");
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, type: ToastType) => {
      let randomPart: number;
      try {
        if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
          const array = new Uint32Array(1);
          const result = crypto.getRandomValues(array);
          if (result && result.length > 0) {
            randomPart = result[0];
          } else {
            randomPart =  Math.floor(Date.now() / 1000);
          }
        } else {
          randomPart =  Math.floor(Date.now() / 1000);
        }
      } catch {
        randomPart =  Math.floor(Date.now() / 1000);
      }
      const id = `${Date.now()}-${randomPart}`;
      const newToast: ToastMessage = { id, message, type };

      setToasts((prevToasts) => [...prevToasts, newToast]);

      setTimeout(() => {
        removeToast(id);
      }, 5000);
    },
    [removeToast]
  );

  const toastProviderValue = useMemo(
    () => ({ addToast, removeToast, toasts }),
    [addToast, removeToast, toasts]
  );

  return (
    <ToastContext.Provider value={toastProviderValue}>
      {children}
      <div className="stacked-toasts-container">
        <StackedToasts
          toasts={toasts.map((toast) => ({
            id: toast.id,
            message: toast.message,
            variant: toast.type,
            onClose: removeToast,
          }))}
          onRemove={removeToast}
          position={ToastPositionEnum.BottomRight}
        />
      </div>
    </ToastContext.Provider>
  );
};

